import {
  DialogForm,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  LineColorPicker,
  useDialogExitWarning,
} from '@ais-ui/ui-core';
import React, { useCallback } from 'react';
import { GroupMultiSelect } from '../../../Groups/GroupMultiSelect';
import { useLocalization } from '@ais-ui/locale-management';
import { useSaveShiplist } from './useSaveShiplist';
import { ShiplistConfirmationDialog } from './ShiplistConfirmationDialog';
import { useMutateShiplistContext } from './MutateShiplistContext';
import { AddShipsToListFormField } from './AddShipsToListFormField';
import { useOwnershipCheck } from '../../../utils/useOwnershipCheck';

const SaveShiplistForm = () => {
  const { messages } = useLocalization();
  const { shiplist, form, action } = useMutateShiplistContext();
  const { isOwner } = useOwnershipCheck();

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shiplistsSaveShiplistNameLabel} *</FormLabel>
              <InfoIconTooltip>{messages.shiplistsSaveNameOfShiplistTooltip}</InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Input
                disabled={shiplist !== undefined}
                id="shiplist-name"
                type="text"
                placeholder={messages.shiplistsSaveShiplistNamePlaceholder}
                autoFocus={shiplist === undefined}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        key="color"
        control={form.control}
        name="color"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shiplistsSaveColorLabel} *</FormLabel>
              <InfoIconTooltip>{messages.shiplistsSaveColorPlaceholder}</InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <LineColorPicker
                background={field.value}
                setBackground={(color) => {
                  form.setValue('color', color);
                }}
                className="flex-shrink-0 flex-grow-0 w-12 p-0"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        key="selectedGroups"
        control={form.control}
        name="selectedGroups"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shiplistsSaveShareLabel}</FormLabel>
              <InfoIconTooltip>{messages.shiplistsSaveShareTooltip}</InfoIconTooltip>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <GroupMultiSelect
                {...field}
                disabled={action === 'edit' && !isOwner(shiplist)}
                disabledReason={
                  messages.genericNotOwnerDisabledText ||
                  'You must be the owner to share this object'
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <AddShipsToListFormField></AddShipsToListFormField>
    </>
  );
};

export function SaveShiplistDialog() {
  const { messages } = useLocalization();

  const { showDialog, setShowDialog, action, isLoading, isSubmitting, form } =
    useMutateShiplistContext();

  const {
    onSubmit,
    handleCancel,
    handleConfirm,
    showConfirmDialog,
    setShowConfirmDialog,
  } = useSaveShiplist();
  const [dialogTitle, dialogDescription] =
    action === 'edit'
      ? [messages.shiplistsSaveEditTitle, messages.shiplistsSaveEditSubtitle]
      : [
          messages.shiplistsSaveCreateTitle,
          messages.shiplistsSaveCreateSubtitle,
        ];

  const handleExitConfirm = useCallback(() => {
    setShowDialog(false);
  }, [setShowDialog]);

  const { handleClose, ExitWarningDialog } = useDialogExitWarning({
    onConfirm: handleExitConfirm,
    title: messages.shiplistsWarningTitle || 'Discard shiplist changes?',
    description: messages.shiplistsWarningDescription || 'Your shiplist configuration will be lost. Are you sure you want to close?',
  });

  return (
    <Form {...form}>
      <DialogForm
        showDialog={showDialog}
        onOpenChange={setShowDialog}
        title={dialogTitle}
        description={dialogDescription}
        isLoading={isLoading}
        hasLoaded={!isLoading}
        buttonId="shiplist-submit-button"
        buttonText={
          isSubmitting ? messages.genericSubmitting : messages.genericSubmit
        }
        buttonDisabled={isSubmitting}
        buttonOnClick={form.handleSubmit(onSubmit)}
        observeResize={false}
        className="flex-col-reverse"
        onClose={handleClose}
      >
        <SaveShiplistForm />
      </DialogForm>
      <ShiplistConfirmationDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <ExitWarningDialog />
    </Form>
  );
}
